"use client";

/**
 * Context provider for post page data.
 *
 * Provides post data to all child components, allowing them to
 * access post, related posts, category posts, and related entities
 * without prop drilling.
 */

import React, { createContext, useContext, useMemo } from "react";
import type { Post, Category } from "@/payload-types";
import type { SlimStandingsBlockData } from "./slimStandingsBlockData";
import type { FUSportsMatch, SearchEntityResult } from "fansunited-data-layer";
import type {
    PostPageData,
    RelatedEntity,
    EntityType,
    CompetitorSeo,
    StandingsBlockPreFetchedData,
    MatchListPreFetchedData,
} from "./types";

/**
 * Extended context value with computed helpers.
 */
export interface PostDataContextValue extends PostPageData {
    /** Check if the post has any related posts */
    hasRelatedPosts: boolean;
    /** Check if the post has any category posts */
    hasCategoryPosts: boolean;
    /** Check if the post has any latest posts (fallback) */
    hasLatestPosts: boolean;
    /** Check if the post has any related entities */
    hasRelatedEntities: boolean;
    /** Get entities of a specific type */
    getEntitiesByType: (type: EntityType) => RelatedEntity[];
    /** Get pre-fetched competitor SEO data by entity ID */
    getCompetitorSeo: (entityId: string) => CompetitorSeo | undefined;
    /** Get pre-fetched match data by match ID */
    getMatchData: (matchId: string) => FUSportsMatch | undefined;
    /** Get pre-fetched hydrated competition data by competition ID */
    getStandingsData: (competitionId: string) => SlimStandingsBlockData | undefined;
    /** Get the pre-fetched competition search entity (name + logo) by competition ID */
    getStandingsCompetitionEntity: (competitionId: string) => SearchEntityResult | null | undefined;
    /** Get pre-fetched match list payload by refKey. */
    getMatchListData: (refKey: string) => MatchListPreFetchedData | undefined;
    /** Get all categories from the post */
    categories: Category[];
    /** Get the authors from the post */
    authors: Post["populatedAuthors"];
}

const PostDataContext = createContext<PostDataContextValue | null>(null);

interface PostDataProviderProps {
    data: PostPageData;
    children: React.ReactNode;
}

/**
 * Provider component that makes post data available to all child components.
 *
 * Usage in a server component:
 * ```tsx
 * const data = await getPostPageData({ tenantId, locale, slug });
 *
 * return (
 *     <PostDataProvider data={data}>
 *         <PostLayout />
 *     </PostDataProvider>
 * );
 * ```
 */
export function PostDataProvider({ data, children }: PostDataProviderProps) {
    const contextValue = useMemo<PostDataContextValue>(() => {
        // Extract categories from post
        const categories = (data.post.categories || []).filter((c): c is Category => typeof c !== "number");

        return {
            ...data,
            hasRelatedPosts: data.relatedPosts.length > 0,
            hasCategoryPosts: data.categoryPosts.length > 0,
            hasLatestPosts: data.latestPosts.length > 0,
            hasRelatedEntities: data.relatedEntities.length > 0,
            getEntitiesByType: (type: EntityType) => data.relatedEntities.filter((e) => e.type === type),
            getCompetitorSeo: (entityId: string) => data.competitorSeo.find((p) => p.entityId === entityId),
            getMatchData: (matchId: string) => data.matchCardData.find((m) => m.matchId === matchId)?.data,
            getStandingsData: (competitionId: string) =>
                data.standingsBlockData.find((s) => s.competitionId === competitionId)?.data,
            getStandingsCompetitionEntity: (competitionId: string) =>
                data.standingsBlockData.find((s) => s.competitionId === competitionId)?.competitionEntity,
            getMatchListData: (refKey: string) => data.matchListData.find((m) => m.refKey === refKey),
            categories,
            authors: data.post.populatedAuthors || [],
        };
    }, [data]);

    return <PostDataContext.Provider value={contextValue}>{children}</PostDataContext.Provider>;
}

/**
 * Hook to access post data in client components.
 * Must be used within a PostDataProvider.
 *
 * @throws Error if used outside of PostDataProvider
 *
 * Usage:
 * ```tsx
 * "use client";
 * import { usePostData } from "@/views/post/context";
 *
 * function PostContent() {
 *     const { post, relatedPosts, relatedEntities, hasRelatedEntities } = usePostData();
 *
 *     if (hasRelatedEntities) {
 *         const teams = getEntitiesByType("team");
 *         // Render team cards...
 *     }
 * }
 * ```
 */
export function usePostData(): PostDataContextValue {
    const context = useContext(PostDataContext);

    if (!context) {
        throw new Error("usePostData must be used within a PostDataProvider");
    }

    return context;
}

/**
 * Hook to optionally access post data.
 * Returns null if not within a PostDataProvider (useful for shared components).
 */
export function usePostDataOptional(): PostDataContextValue | null {
    return useContext(PostDataContext);
}
